import {
    all,
    call,
    getContext,
    put,
    race,
    select,
    spawn,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import {
    actions as authActions,
    selectors as authSelectors,
    types as authTypes,
} from '@elab/core/store/auth';
import { selectors as configSelectors } from '@elab/core/store/config';
import { actions as globalActions, selectors as globalSelectors } from '@elab/core/store/global';
import { actions as uiActions, types as uiTypes } from '@elab/core/store/ui';
import { actions as userActions } from '@elab/core/store/user';
import { actions as socialActions, selectors as socialSelectors } from '@elab/core/store/social';
import logger from '@elab/core/utils/logger';
import {
    defineAdditionalAddressFieldsDisplay,
    getConfirmationFromModal,
    initAddressSuggestionWatchers,
} from '@elab/core/utils/address/sagaHelpers';
import { registrationPrefillFields } from '@elab/core/utils/constants';
import analytics from '@elab/core/utils/analytics';
import dynaForms, { cleanPhoneNumber, dtoBuilder } from '@elab/core/utils/dynaForms';
import dynaFormsApi from '@elab/core/utils/dynaForms/api';
import { validateEmailAlreadyExist } from '@elab/core/utils/dynaForms/helpers';
import { doFieldValidation } from '@elab/core/utils/validations/doFieldValidation';
import { getPrefillValue, setPrefillData } from '@elab/core/utils/personalInfo';
import verifyPhoneFormActions from '@elab/components/connected/forms/PhoneVerificationForm/redux/actions';
import verifyPhoneFormSelectors from '@elab/components/connected/forms/PhoneVerificationForm/redux/selectors';
import verifyPhoneFormTypes from '@elab/components/connected/forms/PhoneVerificationForm/redux/types';
import { updateConstraint } from '@elab/core/utils/constraints/sagaHelpers';
import { getSearchParam } from '@elab/core/utils/address';
import { parameters as URLParameters } from '@elab/core/utils/url';
import { runPinCodeChecks } from '@elab/components/connected/forms/PinCodeForm/redux/saga';
import isEmpty from '@elab/core/utils/isEmpty';
import { mask } from '@elab/core/utils/inputMaskHelpers';

import getActions from './actions';
import api from './api';
import getSelectors from './selectors';
import getTypes from './types';
import { getCurrentDate } from './helper';

const { ageVerified } = require('@elab/core/utils/validations');

const NO_LOYALTY = 'NO_LOYALTY';
const PHYSICAL_LOYALTY = 'PHYSICAL_LOYALTY';
const LOYALTY_OPTION_REGISTRATION_FORM = 'loyaltyOptionsForRegistrationForm.value.0.';
const LOYALTY_OPTION_FIELD_NAME = `${LOYALTY_OPTION_REGISTRATION_FORM}loyaltyOptions`;
const PIN_CODE_FIELD_NAME = `${LOYALTY_OPTION_REGISTRATION_FORM}pinCode`;
const LOYALTY_DOB_FIELD_NAME = `${LOYALTY_OPTION_REGISTRATION_FORM}loyaltyDateOfBirth`;
export function* processMarketing(dto, selector) {
    const shouldProcessMarketing = yield select(configSelectors.marketingProcessing);
    if (!shouldProcessMarketing) {
        return dto;
    }

    const processedDTO = { ...dto };
    const communicationField = yield select(selector.getDataFieldValue, 'communication');
    const personalInfo = yield select(selector.getDataFieldValue, 'personalInfo');

    if (!communicationField) {
        processedDTO.gdprServiceInformation.privacyCommunicationChannels = {
            ...dto.gdprServiceInformation.privacyCommunicationChannels,
            email: false,
            whatsApp: false,
            sms: false,
        };
    }
    if (personalInfo) {
        processedDTO.gdprServiceInformation.privacyCommunicationChannels = {
            ...dto.gdprServiceInformation.privacyCommunicationChannels,
            socialMedia: true,
            receivePersonalRecommendations: true,
        };
    }

    if (dto.loyaltyInformation && dto.loyaltyInformation.loyaltyOptions !== NO_LOYALTY) {
        processedDTO.gdprServiceInformation = {
            ...dto.gdprServiceInformation,
            marketingCommAccepted: true,
        };
    }
    if (!dto.loyaltyInformation) {
        processedDTO.loyaltyInformation = {
            ...dto.loyaltyInformation,
            loyaltyOptions: PHYSICAL_LOYALTY,
        };
    }

    return processedDTO;
}

export function processLoyaltyOptions(dto) {
    const processedDTO = { ...dto };

    if (
        dto.loyaltyInformation &&
        dto.loyaltyInformation.loyaltyOptions == PHYSICAL_LOYALTY &&
        dto.loyaltyInformation.cardNumber
    ) {
        processedDTO.loyaltyInformation = {
            ...dto.loyaltyInformation,
            activationCode: dto.personalInformation.dateOfBirth.split('T')[0],
        };
    }

    return processedDTO;
}

export function* customDtoBuilder(data, dataset = {}, selectors) {
    let dto = yield call(dtoBuilder, data, dataset);
    dto = yield call(processMarketing, dto, selectors);
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (isNewLoyaltyFlow) {
        dto = yield call(processLoyaltyOptions, dto);
    }
    return dto;
}

export function* registrationErrorHandlings(
    actions,
    selectors,
    checkoutRegistration,
    cardNumberField,
    error,
) {
    const metadata = yield select(selectors.getMetadata);
    const stepMetaDataFields = metadata[1].fields;

    const parentFormType = checkoutRegistration
        ? { parentFormId: 'checkout-registration' }
        : { parentFormId: 'registrationForm' };
    yield put(verifyPhoneFormActions.resetForm(parentFormType));
    yield put(verifyPhoneFormActions.phoneVerificationClose());

    for (const field in stepMetaDataFields) {
        if (
            stepMetaDataFields[field].type !== 'HIDDEN' &&
            stepMetaDataFields[field].name !== 'loyaltyOptionsForRegistrationForm'
        ) {
            if (stepMetaDataFields[field].name === 'mobileNumber') {
                yield put(actions.setDataFieldValue(field, '(5', true));
                yield put(
                    actions.setDataFieldReadonlyStatus({
                        name: 'mobileNumber',
                        status: false,
                    }),
                );
            } else {
                yield put(actions.setDataFieldValue(field, null, true));
                yield put(actions.setDataFieldValue(cardNumberField, null, true));
                yield put(verifyPhoneFormActions.setDataFieldValue('code', null, true));
                yield put(
                    actions.setDataFieldReadonlyStatus({
                        name: field,
                        status: false,
                    }),
                );
            }
        }
    }
    yield put(
        verifyPhoneFormActions.setDataFieldReadonlyStatus({
            name: 'code',
            status: false,
        }),
    );
    yield put(actions.disableStep1Button({ disableStep1Button: false }));
    yield put(actions.showPincode(false));
    yield put(actions.setStep(1));
    yield put(actions.setLoyaltyFlag(false));

    if (error.json.errors[0].subject == 'socialProfile.accessToken') {
        const fbAuthId = yield select(authSelectors.getFbAuthId);
        sessionStorage.removeItem(`fbssls_${fbAuthId}`);
        sessionStorage.removeItem(`googleAuthToken`);
        yield put(actions.setDataFieldValue('socialProfileId', null, true));
        yield put(actions.setDataFieldValue('socialProfileAccessToken', null, true));
        yield put(actions.setDataFieldValue('socialProfileProvider', null, true));
        yield put(socialActions.setInitialSocialInfo());
        const fbButtonNode = document.querySelectorAll('.fb-lgn-btn');
        fbButtonNode.forEach(element => {
            element.classList.remove('social-login--disable');
        });
        const googleButtonNode = document.querySelectorAll('.social-login--google');
        googleButtonNode.forEach(element => {
            element.classList.remove('google-login--disable');
        });
    }
}

export const setDataField = ({ actions }) =>
    function*(action) {
        const name = action.payload.name.split('.').pop();
        if (name === 'dateOfBirth') {
            yield put(actions.resetAgeVerification());
        }
    };

function* resetDataFields(actions, stepMetaDataFields) {
    for (const field in stepMetaDataFields) {
        if (stepMetaDataFields[field].type !== 'HIDDEN')
            yield put(actions.setDataFieldValue(field, null, true));
    }
}

export const stepBack = ({ actions, selectors }) =>
    function*() {
        const metadata = yield select(selectors.getMetadata);
        const step = yield select(selectors.getStep);
        const currMetadataFields = metadata[step - 1].fields;
        if (currMetadataFields && currMetadataFields.password && step === 1) {
            for (const field of ['password', 'passwordConfirm']) {
                yield put(actions.setDataFieldValue(field, '', true));
            }
            actions.setIsLoyalty(false);
        }
        const resetSecondStepDataFields = yield select(
            configSelectors.shouldResetSecondStepDataFields,
        );
        if (resetSecondStepDataFields) {
            const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
            const loyaltyOptionsValue = yield select(
                selectors.getDataFieldValue,
                `${loyaltyPrefix}loyaltyOptions`,
            );
            if (loyaltyOptionsValue === PHYSICAL_LOYALTY) {
                const secondStepMetaDataFields = metadata[step].fields;
                yield call(resetDataFields, actions, secondStepMetaDataFields);
            }
        }
        const shouldCleanPhoneNumber = yield select(configSelectors.cleanPhoneNumber);
        if (shouldCleanPhoneNumber && currMetadataFields && currMetadataFields.phone) {
            const reachedPhone = yield select(selectors.getDataFieldValue, 'phone');
            const maskConfig = { mask: currMetadataFields.phone.mask };
            const maskedPhone = yield call(mask, reachedPhone, maskConfig);
            yield put(actions.setDataFieldValue('phone', maskedPhone, true));
        }
    };

export const stepSubmit = ({ selectors }) =>
    function*() {
        const step = yield select(selectors.getStep);
        const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
        const cardNumber = yield select(selectors.getDataFieldValue, `${loyaltyPrefix}cardNumber`);
        yield call(analytics.handleRegistrationStep, step, cardNumber);
    };

export function* handleCardNumberInitialization({ actions, selectors }) {
    const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
    const loyaltyOptionsValue = yield select(
        selectors.getDataFieldValue,
        `${loyaltyPrefix}loyaltyOptions`,
    );
    const cardNumberField = `${loyaltyPrefix}cardNumber`;
    const activationCodeField = `${loyaltyPrefix}activationCode`;
    const pinCodeField = `${loyaltyPrefix}pinCode`;
    const loyaltyDateOfBirthField = `${loyaltyPrefix}loyaltyDateOfBirth`;
    const cardNumber = yield select(selectors.getDataFieldValue, cardNumberField);
    const activationCode = yield select(selectors.getDataFieldValue, activationCodeField);
    const pinCode = yield select(selectors.getDataFieldValue, pinCodeField);
    const loyaltyDateOfBirth = yield select(selectors.getDataFieldValue, loyaltyDateOfBirthField);

    const isLoyaltyPhone = yield select(selectors.isLoyaltyPhone);
    if (!isLoyaltyPhone && loyaltyOptionsValue !== 'PHYSICAL_LOYALTY') {
        if (cardNumber !== undefined) {
            yield put(actions.setDataFieldValue(cardNumberField, undefined, true));
        }
        if (activationCode !== undefined) {
            yield put(actions.setDataFieldValue(activationCodeField, undefined, true));
        }
        if (pinCode !== undefined) {
            yield put(actions.setDataFieldValue(pinCodeField, undefined, true));
        }
        if (loyaltyDateOfBirth !== undefined) {
            yield put(actions.setDataFieldValue(loyaltyDateOfBirthField, undefined, true));
        }
        yield put(
            actions.setIsPinCodeOrDOBValid({
                isPinCodeOrDOBValid: true,
                isPinCodeAndDOBFilled: false,
            }),
        );
    } else if (!isLoyaltyPhone) {
        if (cardNumber === undefined) {
            yield put(actions.setDataFieldValue(cardNumberField, null, true));
        }
        if (activationCode === undefined) {
            yield put(actions.setDataFieldValue(activationCodeField, null, true));
        }
        if (!pinCode) {
            yield put(actions.setDataFieldValue(pinCodeField, null, true));
        }
        if (!loyaltyDateOfBirth) {
            yield put(actions.setDataFieldValue(loyaltyDateOfBirthField, null, true));
        }
    }
}

export const radioButtonChanges = ({ actions, selectors }) =>
    function*(action) {
        const fieldName = action.payload.name;
        const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
        const loyaltyOptionsField = `${loyaltyPrefix}loyaltyOptions`;
        if (fieldName !== loyaltyOptionsField) return;

        const loyaltyOptionsValue = yield select(selectors.getDataFieldValue, loyaltyOptionsField);
        const values = yield select(selectors.getDataFieldValues, loyaltyOptionsField);
        const { label } = values.find(val => val.name === loyaltyOptionsValue);
        yield call(analytics.handleRegistrationLoyaltyOption, label);

        yield call(handleCardNumberInitialization, { actions, selectors });
        yield put(actions.showPincode(false));
    };

export function* populateNestedForm({ actions }) {
    const shouldPopulate = yield select(configSelectors.registrationShouldPopulateNest);
    if (shouldPopulate) {
        const name = yield select(configSelectors.registrationNestedFormField);
        yield put(actions.addFieldItem({ name }));
    }
}

export function* verifyPhone(DTO, selectors, actions, dialogInfo) {
    yield put(verifyPhoneFormActions.showPhoneVerificationField());
    const { canContinue } = yield race({
        canContinue: take(verifyPhoneFormTypes.SUCCESS_VERIFIED),
        cancel: take(verifyPhoneFormTypes.EDIT_PHONE),
        close: take(verifyPhoneFormTypes.PHONE_VERIFICATION_CLOSE),
    });
    if (!canContinue) {
        return;
    }

    let sessionField = yield select(selectors.getDataField, 'phoneVerificationSessionId');
    if (sessionField && !sessionField.value) {
        sessionField.value = yield select(verifyPhoneFormSelectors.getSessionId);
    }
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (isNewLoyaltyFlow) {
        yield put(actions.phoneVerificationDone());
    } else {
        DTO = yield call(dtoBuilder, { sessionField }, DTO);
        const successRedirectUrl = yield select(selectors.getSuccessRedirectLink);
        yield call(doRegister, DTO, successRedirectUrl, { actions, selectors }, dialogInfo);
    }
}

export const initialize = ctx =>
    function*() {
        yield call(updateConstraintsByInitialize);
        yield call(populateNestedForm, ctx);
        yield call(handleCardNumberInitialization, ctx);
        const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
        if (isNewLoyaltyFlow) {
            // Need to place this check in order to remove readonly attribute from first step
            yield put(ctx.actions.phoneDobReceived({ isLoyalty: false, forceReadOnly: false }));
        }
        const socialData = yield select(socialSelectors.getSocialData);
        if (socialData && socialData.socialProfile) {
            yield put(ctx.actions.disableStep1Button({ disableStep1Button: false }));
            yield call(setPrefillData, ctx, socialData);
        }
        const step = yield select(ctx.selectors.getStep);
        yield call(analytics.handleCreateAccount, step);
    };

function* updateConstraintsByInitialize() {
    const country = yield select(globalSelectors.getCountry);
    const shouldValidateByAge = yield select(configSelectors.shouldValidateByAge, country);
    if (!shouldValidateByAge) {
        return;
    }

    yield call(updateConstraint, 'dateOfBirth', 'ageVerification');
}

export function* setDataFieldValue({ payload }) {
    yield call(updateConstraintsBySetFieldValue, payload);
}

function* updateConstraintsBySetFieldValue({ name: fieldName, value: fieldValue }) {
    const { storeName: formStoreName } = yield getContext('ctx');
    const country = yield select(globalSelectors.getCountry);
    const rule = yield select(
        configSelectors.getFilterConstraintsRule,
        formStoreName,
        country,
        fieldName,
    );
    if (!rule) return;

    const shouldAdd = fieldValue !== rule.conditionValue;

    yield call(updateConstraint, rule.targetField, rule.constraintType, shouldAdd);
    yield call(doFieldValidation, rule.targetField, true);
}

export function* beforeStepValidation({ selectors, actions }) {
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    const shouldValidatePinOrDOB = yield select(configSelectors.shouldValidatePinOrDOB);
    const step = yield select(selectors.getStep);
    const isLoyaltyPhone = yield select(selectors.isLoyaltyPhone);
    if (isNewLoyaltyFlow && isLoyaltyPhone) {
        if (step === 2) {
            const dateOfBirth = yield select(selectors.getDataFieldValue, 'dateOfBirth');
            yield call(setActivationCode, actions, dateOfBirth);
        }
    }
    if (shouldValidatePinOrDOB) {
        const loyaltyOption = yield select(selectors.getDataFieldValue, LOYALTY_OPTION_FIELD_NAME);
        const pinCode = yield select(selectors.getDataFieldValue, PIN_CODE_FIELD_NAME);
        const loyaltyDateOfBirth = yield select(
            selectors.getDataFieldValue,
            LOYALTY_DOB_FIELD_NAME,
        );
        const isBothNotEmpty = !!(pinCode && loyaltyDateOfBirth);
        const isValid =
            loyaltyOption !== PHYSICAL_LOYALTY ||
            !!(pinCode ? !loyaltyDateOfBirth : loyaltyDateOfBirth);
        yield put(
            actions.setIsPinCodeOrDOBValid({
                isPinCodeOrDOBValid: isValid,
                isPinCodeAndDOBFilled: isBothNotEmpty,
            }),
        );
        return isValid;
    }
    return true;
}

export const setStep = ({ selectors, actions }) =>
    function*() {
        const step = yield select(selectors.getStep);
        const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
        if (step === 1 && isNewLoyaltyFlow) {
            yield put(actions.phoneDobReceived({ isLoyalty: false, forceReadOnly: false }));
        }
        const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
        const cardNumber = yield select(selectors.getDataFieldValue, `${loyaltyPrefix}cardNumber`);

        yield call(analytics.handleCreateAccount, step, !!cardNumber);
    };

export const doSubmitWithoutPhoneVerification = ({ actions, selectors, storeName }) =>
    function*() {
        const step = yield select(selectors.getStep);
        yield put(actions.setStep(step + 1));
        const dialogInfo = {};
        if (storeName === 'registrationForm') {
            dialogInfo.name = 'registrationSuccess';
        } else if (storeName === 'checkout-registration') {
            dialogInfo.name = 'checkout-registration-success';
        }
        yield put({ type: `${storeName}.SUBMIT`, payload: { dialogInfo } });
    };

export function* doSubmit(
    DTO,
    { actions, selectors },
    submitUrl,
    dialogInfo = { dialogName: 'registrationSuccess' },
) {
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (isNewLoyaltyFlow) {
        const successRedirectUrl = yield select(selectors.getSuccessRedirectLink);
        yield call(doRegister, DTO, successRedirectUrl, { actions, selectors }, dialogInfo);
    } else {
        const shouldVerifyPhone = !isEmpty(yield select(verifyPhoneFormSelectors.getData));
        if (shouldVerifyPhone) {
            yield spawn(verifyPhone, DTO, selectors, actions, dialogInfo);
        } else {
            const prevPageData = yield select(globalSelectors.getPrevPageData);
            const shouldRedirectToPDPAfterLogin = yield select(
                configSelectors.shouldRedirectToPDPAfterLogin,
            );
            let successRedirectUrl = yield select(selectors.getSuccessRedirectLink);
            if (shouldRedirectToPDPAfterLogin && prevPageData && prevPageData.url) {
                successRedirectUrl = prevPageData.url;
            }
            yield call(doRegister, DTO, successRedirectUrl, { actions, selectors }, dialogInfo);
        }
    }
}

export function* doRegister(
    registrationDTO,
    successRedirectLink,
    { actions, selectors },
    dialogInfo = {},
    checkoutRegistration = false,
) {
    const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
    const cardNumberField = `${loyaltyPrefix}cardNumber`;
    registrationDTO.uid = registrationDTO.uid.toLowerCase();
    let registeredUserResult;
    try {
        registeredUserResult = yield call(api.doRegister, registrationDTO);
    } catch (error) {
        const socialProviderType = registrationDTO.socialProfile.provider
            .toLowerCase()
            .replace(/\b\w/g, s => s.toUpperCase());
        const socialError = error.json.errors[0].message.replaceAll(
            '{socialProvider}',
            socialProviderType,
        );
        yield put(actions.setGlobalError(socialError, error.json.errors[0].type));
        yield call(
            registrationErrorHandlings,
            actions,
            selectors,
            checkoutRegistration,
            cardNumberField,
            error,
        );
    }

    if (!registeredUserResult) {
        return;
    }
    yield call(analytics.handleRegistration, registeredUserResult);
    yield put(actions.setRegisteredUser(registeredUserResult));
    yield call(analytics.CustomerRegistrationEvent);

    const showPopup = yield select(configSelectors.showPopup);
    const { uid, password } = registrationDTO;
    yield put(
        authActions.doLogin({
            username: uid,
            password,
            preventRedirects: checkoutRegistration || showPopup, // when registration on checkout flow - should prevent standard redirect inside doLogin
            context: 'after-registration',
        }),
    );
    yield take(authTypes.LOGIN_DONE);
    // Event will be listened by insider for logging customer registration event.
    const event = new CustomEvent('customer-register', { detail: true });
    window.dispatchEvent(event);
    if (checkoutRegistration) {
        yield put(userActions.setRegisteredOnCheckout(true));
    }

    if (showPopup ? dialogInfo.name : dialogInfo.iframeUrl) {
        dialogInfo.iframeRedirectOnClose = successRedirectLink;
        const metadata = yield select(selectors.getMetadata);
        const stepMetaDataFields = metadata[1].fields;
        for (const field in stepMetaDataFields) {
            if (
                stepMetaDataFields[field].type !== 'HIDDEN' &&
                stepMetaDataFields[field].name !== 'loyaltyOptionsForRegistrationForm'
            ) {
                if (stepMetaDataFields[field].name === 'mobileNumber') {
                    yield put(actions.setDataFieldValue(field, '(5', true));
                } else {
                    yield put(actions.setDataFieldValue(field, null, true));
                    yield put(actions.setDataFieldValue(cardNumberField, null, true));
                    yield put(verifyPhoneFormActions.setDataFieldValue('code', null, true));
                }
            }
        }
        yield put(actions.disableStep1Button({ disableStep1Button: true }));
        yield put(uiActions.openDialog(dialogInfo));
    } else {
        const url = yield call(getSearchParam, URLParameters.REDIRECT_TO_TOKYWOKY);
        yield put(globalActions.redirect(url || successRedirectLink));
    }
}

function* setActivationCode(actions, value) {
    yield put(
        actions.setDataFieldValue(
            'loyaltyOptionsForRegistrationForm.value.0.activationCode',
            value,
            true,
        ),
    );
}

export function* beforeStepIncrement({ selectors, actions }) {
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (isNewLoyaltyFlow) {
        const step = yield select(selectors.getStep);
        if (step === 1) {
            const data = yield select(selectors.getData);
            const shouldCleanPhoneNumber = yield select(configSelectors.cleanPhoneNumber);
            let dto = yield call(customDtoBuilder, data, {}, selectors);
            if (dto && shouldCleanPhoneNumber) {
                dto = cleanPhoneNumber(dto);
            }
            const shouldVerifyPhone = !isEmpty(yield select(verifyPhoneFormSelectors.getData));
            if (shouldVerifyPhone) {
                yield spawn(verifyPhone, dto, selectors, actions, {});
                const { canContinue, canAlsoContinue } = yield race({
                    canContinue: take('registrationForm.PHONE_VERIFICATION_DONE'),
                    canAlsoContinue: take('checkout-registration.PHONE_VERIFICATION_DONE'),
                    cancel: take(verifyPhoneFormTypes.EDIT_PHONE),
                    close: take(verifyPhoneFormTypes.PHONE_VERIFICATION_CLOSE),
                });

                if (canContinue || canAlsoContinue) {
                    const phoneVerificationSessionId = yield select(
                        selectors.getDataField,
                        'phoneVerificationSessionId',
                    );
                    if (phoneVerificationSessionId && !phoneVerificationSessionId.value) {
                        phoneVerificationSessionId.value = yield select(
                            verifyPhoneFormSelectors.getSessionId,
                        );
                    }
                    const verificationResult = yield call(api.verifyUserByPhone, {
                        phone: dto.defaultAddress.mobileNumber,
                        phoneVerificationSessionId: phoneVerificationSessionId.value,
                    });
                    let isLoyalty = false;
                    if (verificationResult.cardNumber) {
                        if (verificationResult.activationCode) {
                            yield put(actions.setDataFieldType('dateOfBirth', 'HIDDEN'));
                        }
                        yield call(setRegistrationField, actions, verificationResult);
                        isLoyalty = true;
                    }
                    yield put(actions.phoneDobReceived({ isLoyalty, forceReadOnly: true }));
                    if (!verificationResult.errors) {
                        yield call(forceLoyaltyOption, actions, selectors);
                    }
                } else {
                    // we don't want user to move ahead
                    return false;
                }
            } else {
                yield put(actions.phoneDobReceived({ isLoyalty: false, forceReadOnly: true }));
            }
        }
    }
    return true;
}

function* forceLoyaltyOption(actions, selectors) {
    const option = yield select(
        selectors.getDataFieldValue,
        'loyaltyOptionsForRegistrationForm.value.0.loyaltyOptions',
    );
    // Forcing UI to update loyalty option
    yield put(
        actions.setDataFieldValue(
            'loyaltyOptionsForRegistrationForm.value.0.loyaltyOptions',
            'NO_LOYALTY',
            true,
        ),
    );
    yield put(
        actions.setDataFieldValue(
            'loyaltyOptionsForRegistrationForm.value.0.loyaltyOptions',
            option,
            true,
        ),
    );
}

export function* setRegistrationField(actions, verificationResult) {
    yield put(
        actions.setDataFieldValue(
            'loyaltyOptionsForRegistrationForm.value.0.cardNumber',
            verificationResult.cardNumber,
            true,
        ),
    );
    yield call(setActivationCode, actions, verificationResult.activationCode || getCurrentDate());
    yield put(actions.setDataFieldValue('dateOfBirth', verificationResult.activationCode, true));
    yield put(actions.setDataFieldValue('pinCode', verificationResult.pinCode));
}

export function* needConsent({ selectors }) {
    const isAlreadyVerified = yield select(selectors.isAgeVerified);
    const dateOfBirth = yield select(selectors.getAgeVerification);
    const setDateOfBirth = yield select(selectors.getDataFieldValue, 'dateOfBirth');
    return (
        dateOfBirth &&
        !ageVerified(setDateOfBirth, null, dateOfBirth.parameters) &&
        !isAlreadyVerified
    );
}

function* disableStep1Fields(actions, selectors) {
    const isLoyaltyPhone = yield select(selectors.isLoyaltyPhone);
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (isNewLoyaltyFlow) {
        yield put(actions.phoneDobReceived({ isLoyalty: isLoyaltyPhone, forceReadOnly: true }));
        yield put(actions.disableStep1Button({ disableStep1Button: true }));
    }
}

export function* afterStepValidation({ storeName, selectors, actions, types }) {
    const step = yield select(selectors.getStep);
    const isNewLoyaltyFlow = yield select(configSelectors.newLoyaltyRegistrationFlow);
    if (isNewLoyaltyFlow) {
        yield put(
            actions.setDataFieldReadonlyStatus({
                name: 'mobileNumber',
                status: true,
            }),
        );
    }
    const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);

    const loyaltyOptionsValue = yield select(
        selectors.getDataFieldValue,
        `${loyaltyPrefix}loyaltyOptions`,
    );
    const activationCode = yield select(
        selectors.getDataFieldValue,
        `${loyaltyPrefix}activationCode`,
    );
    yield call(disableStep1Fields, actions, selectors);
    const setTempValidationForLoyalty = yield select(configSelectors.getRegistrationTempValidation);
    const hideDateOfBirthForPhysical = yield select(configSelectors.getHideDateOfBirthForPhysical);

    if (loyaltyOptionsValue !== NO_LOYALTY && (step === 1 || (isNewLoyaltyFlow && step === 2))) {
        // TODO: Remove once we have separate form for registration non-loyalty
        if (setTempValidationForLoyalty) {
            yield put(
                actions.setTemporaryDOBValidation({
                    deferred: false,
                    errorMessage: 'Lütfen doğum tarihinizi girin',
                    parameters: {},
                    type: 'required',
                }),
            );
            yield put(
                actions.setTemporaryGenderValidation({
                    deferred: false,
                    errorMessage: 'Lütfen cinsiyetinizi giriniz',
                    parameters: {},
                    type: 'required',
                }),
            );
        }
        const isLoyaltyPhone = yield select(selectors.isLoyaltyPhone);
        if (!isLoyaltyPhone) {
            const pinStatus = yield call(runPinCodeChecks);
            if (!pinStatus) {
                return false;
            }
        }
    }
    if (
        hideDateOfBirthForPhysical &&
        activationCode &&
        (step === 1 || step === 2 || isNewLoyaltyFlow)
    ) {
        yield put(actions.setDataFieldValue('dateOfBirth', activationCode));
    } else {
        yield put(actions.setDataFieldType('dateOfBirth', 'DATE'));
    }
    const metadata = yield select(selectors.getMetadata);
    let canContinue = true;
    try {
        const useAgeVerification = yield select(configSelectors.useAgeVerification);
        if (
            useAgeVerification &&
            metadata.length >= step &&
            metadata[step - 1].fields.dateOfBirth
        ) {
            const consentNeeded = yield call(needConsent, { selectors });

            if (consentNeeded) {
                yield put(uiActions.showModal(types.CONSENT_NEEDED_DIALOG));
                const modalAction = yield take([uiTypes.OK_MODAL, uiTypes.CANCEL_MODAL]);

                if (modalAction.type === uiTypes.OK_MODAL) {
                    yield put(actions.setAgeVerification());
                } else {
                    canContinue = false;
                }
            }
        }

        if (
            !(yield call(getConfirmationFromModal, {
                types,
                actions,
                selectors,
            }))
        ) {
            return false;
        }

        const nestedFieldName = yield select(configSelectors.registrationNestedFormField);
        const nestedForm = nestedFieldName
            ? yield select(selectors.getDataFieldValue, nestedFieldName)
            : null;
        const data = yield select(selectors.getData);

        if (metadata[step] && metadata.length > step && metadata[step].prefilled) {
            const DTO = nestedForm
                ? yield call(dtoBuilder, ...nestedForm)
                : yield call(dtoBuilder, data);

            const res = yield call(api.getPrefillInformation, DTO);

            yield call(defineAdditionalAddressFieldsDisplay, {
                storeName,
                valuesSource: res,
                extractValuesWith: getPrefillValue,
            });

            const preventPrefillFieldsByForm = yield select(
                configSelectors.getPreventPrefillFieldsByForm,
                storeName,
            );

            yield all(
                registrationPrefillFields
                    .filter(fieldName => !preventPrefillFieldsByForm.includes(fieldName))
                    .map(fieldName => {
                        const prefilledValue = getPrefillValue(res, fieldName);
                        const preventPrefillFalsyValues = select(
                            configSelectors.getShouldPreventPrefillFalsyValues,
                        );
                        if (!preventPrefillFalsyValues || prefilledValue) {
                            return put(actions.setDataFieldValue(fieldName, prefilledValue, true));
                        }
                    }),
            );
        }
    } catch (e) {
        yield call(logger.error, e);
    }

    if (step === 1) {
        const loyaltyPrefix = yield select(configSelectors.registrationLoyaltyPrefix);
        if (loyaltyPrefix) {
            const cardNumber = yield select(
                selectors.getDataFieldValue,
                `${loyaltyPrefix}cardNumber`,
            );
            yield call(analytics.handleRegistrationStepComplete, step, !!cardNumber);
        }
    }
    return canContinue;
}

export default storeName => (formId, mockmetadata) => {
    const actions = getActions(storeName);
    const selectors = getSelectors(storeName);
    const types = getTypes(storeName);
    const ctx = { storeName: formId || storeName, actions, selectors, types };
    return [
        ...dynaForms.createSagaFor(
            ctx,
            mockmetadata || dynaFormsApi.getFormMetadata(formId || storeName),
            doSubmit,
            afterStepValidation,
            true,
            customDtoBuilder,
            beforeStepValidation,
            undefined,
            validateEmailAlreadyExist,
            beforeStepIncrement,
        ),
        takeEvery(types.DATA_FIELD_VALIDATION_DONE, setDataField(ctx)),
        takeLatest(types.SET_STEP_BACK, stepBack(ctx)),
        takeEvery(types.SUBMIT, stepSubmit(ctx)),
        handleCardNumberInitialization(ctx),
        takeEvery(types.SET_DATA_FIELD_VALUE, radioButtonChanges(ctx)),
        takeEvery(types.SET_DATA_FIELD_VALUE, setDataFieldValue),
        takeEvery(types.AFTER_INITIALIZE, initialize(ctx)),
        takeEvery(types.SET_STEP, setStep(ctx)),
        takeEvery(types.SUBMIT_WITHOUT_PHONE_VERIFICATION, doSubmitWithoutPhoneVerification(ctx)),
        initAddressSuggestionWatchers(ctx),
    ];
};
